/**
 * Ajustes del policlínico — área del tenant.
 *
 * Lo que el administrador puede cambiar de su propio policlínico sin pasar por
 * la plataforma: zona horaria, nombre comercial y color de marca. La identidad
 * fiscal (RUC, razón social, slug) y el plan se muestran pero no se editan:
 * son identidad y relación comercial, y el backend tampoco los acepta.
 *
 * Requiere tenant:read para entrar; tenant:update para guardar.
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Check, Clock, Palette } from "lucide-react";
import {
  type MiPoliclinico,
  getMiPoliclinico,
  updateMiPoliclinico,
} from "../api/tenants";
import { formatApiError } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { fechaHora } from "../lib/fecha";
import { partirNombre } from "../lib/marca";
import { Alert, Button, Card, CardBody, CardHeader, Field, Input } from "../components/ui";

/**
 * Zonas que se ofrecen primero. El resto sale de `Intl.supportedValuesOf`,
 * que trae la base IANA completa del navegador; si no está disponible nos
 * quedamos con esta lista, que cubre a quién le vendemos hoy.
 */
const ZONAS_FRECUENTES = [
  "America/Lima",
  "America/Bogota",
  "America/Guayaquil",
  "America/La_Paz",
  "America/Santiago",
  "America/Argentina/Buenos_Aires",
  "America/Mexico_City",
  "America/New_York",
  "Europe/Madrid",
];

function todasLasZonas(): string[] {
  const intl = Intl as typeof Intl & {
    supportedValuesOf?: (k: string) => string[];
  };
  const todas = intl.supportedValuesOf?.("timeZone") ?? [];
  const resto = todas.filter((z) => !ZONAS_FRECUENTES.includes(z));
  return [...ZONAS_FRECUENTES, ...resto];
}

/** "GMT-5" y la hora actual en esa zona, para no elegir a ciegas. */
function describeZona(zona: string): string {
  try {
    const ahora = new Date().toLocaleTimeString("es-PE", {
      timeZone: zona,
      timeStyle: "short",
    });
    return `ahora son las ${ahora}`;
  } catch {
    return "";
  }
}

export function AjustesPage() {
  const { me } = useAuth();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [guardado, setGuardado] = useState(false);

  const puedeEditar = me?.is_super_admin || (me?.permisos.includes("tenant:update") ?? false);

  const { data: tenant, isLoading } = useQuery({
    queryKey: ["mi-policlinico"],
    queryFn: getMiPoliclinico,
  });

  const [zona, setZona] = useState("");
  const [nombre, setNombre] = useState("");
  const [color, setColor] = useState("");
  const [colorSec, setColorSec] = useState("");
  const [lema, setLema] = useState("");

  // Rellena el formulario cuando llega el dato, sin pisar lo que se esté editando.
  useEffect(() => {
    if (!tenant) return;
    setZona((z) => z || tenant.zona_horaria);
    setNombre((n) => n || tenant.nombre_comercial || "");
    setColor((c) => c || tenant.color_primario || "#1D4ED8");
    setColorSec((c) => c || tenant.color_secundario || tenant.color_primario || "#1D4ED8");
    setLema((l) => l || tenant.lema || "");
  }, [tenant]);

  const guardar = useMutation({
    mutationFn: () =>
      updateMiPoliclinico({
        zona_horaria: zona,
        nombre_comercial: nombre.trim() || null,
        color_primario: color,
        color_secundario: colorSec,
        lema: lema.trim() || null,
      }),
    onSuccess: (actualizado: MiPoliclinico) => {
      queryClient.setQueryData(["mi-policlinico"], actualizado);
      // El branding alimenta el header, el color y la zona con la que se
      // pintan TODAS las horas: hay que refrescarlo o la app queda desfasada.
      queryClient.invalidateQueries({ queryKey: ["tenant-branding"] });
      setError(null);
      setGuardado(true);
      setTimeout(() => setGuardado(false), 2500);
    },
    onError: (e) => setError(formatApiError(e, "No se pudieron guardar los ajustes.")),
  });

  const sucio =
    tenant != null &&
    (zona !== tenant.zona_horaria ||
      nombre.trim() !== (tenant.nombre_comercial ?? "") ||
      color !== (tenant.color_primario ?? "#1D4ED8") ||
      colorSec !== (tenant.color_secundario ?? tenant.color_primario ?? "#1D4ED8") ||
      lema.trim() !== (tenant.lema ?? ""));

  if (isLoading || !tenant) {
    return <div className="text-sm text-slate-400">Cargando…</div>;
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Ajustes del policlínico</h1>
        <p className="mt-1 text-sm text-slate-500">
          Configuración general de {tenant.nombre_comercial ?? tenant.razon_social}.
        </p>
      </div>

      {error && <Alert>{error}</Alert>}

      <Card>
        <CardHeader>
          <h2 className="flex items-center gap-2 font-semibold text-slate-900">
            <Clock className="h-4 w-4 text-[color:var(--color-link)]" aria-hidden="true" />
            Zona horaria
          </h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Determina en qué hora se leen los horarios de atención y las citas. En la base
            de datos todo se guarda en UTC; esto solo cambia cómo se muestra y cómo se
            interpretan los horarios que configuras.
          </p>
        </CardHeader>
        <CardBody className="space-y-3">
          <Field label="Zona" htmlFor="zona" hint={describeZona(zona)}>
            <select
              id="zona"
              value={zona}
              disabled={!puedeEditar}
              onChange={(e) => setZona(e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--color-link)] disabled:bg-slate-50 disabled:text-slate-500"
            >
              {todasLasZonas().map((z) => (
                <option key={z} value={z}>
                  {z.replace(/_/g, " ")}
                </option>
              ))}
            </select>
          </Field>

          {tenant.zona_horaria !== zona && (
            <Alert variant="info">
              Cambiar la zona no mueve las citas ya agendadas: siguen ocurriendo en el
              mismo instante, pero se mostrarán con la hora de {zona.replace(/_/g, " ")}.
              Revisa los horarios de tus profesionales después de guardar.
            </Alert>
          )}

          <p className="text-xs text-slate-500">
            Hora actual del policlínico:{" "}
            <span className="font-mono text-slate-700">
              {fechaHora(new Date().toISOString(), zona || tenant.zona_horaria)}
            </span>
          </p>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <h2 className="flex items-center gap-2 font-semibold text-slate-900">
            <Palette className="h-4 w-4 text-[color:var(--color-link)]" aria-hidden="true" />
            Marca
          </h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Nombre, lema y colores con los que se escribe tu logotipo en el login.
          </p>
        </CardHeader>
        <CardBody className="space-y-4">
          <Field label="Nombre comercial" htmlFor="nombre">
            <Input
              id="nombre"
              value={nombre}
              disabled={!puedeEditar}
              onChange={(e) => setNombre(e.target.value)}
              placeholder={tenant.razon_social}
            />
          </Field>

          <Field
            label="Lema"
            htmlFor="lema"
            hint="La frase que acompaña al nombre en tu logotipo. Se muestra bajo el logo en el login."
          >
            <Input
              id="lema"
              value={lema}
              disabled={!puedeEditar}
              onChange={(e) => setLema(e.target.value)}
              maxLength={120}
              placeholder="Para cuidarlo siempre"
            />
          </Field>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Color principal" htmlFor="color">
              <div className="flex items-center gap-3">
                <input
                  id="color"
                  type="color"
                  value={color}
                  disabled={!puedeEditar}
                  onChange={(e) => setColor(e.target.value)}
                  className="h-9 w-14 cursor-pointer rounded border border-slate-300 disabled:cursor-default"
                />
                <span className="font-mono text-sm text-slate-600">{color}</span>
              </div>
            </Field>

            <Field label="Color secundario" htmlFor="color-sec">
              <div className="flex items-center gap-3">
                <input
                  id="color-sec"
                  type="color"
                  value={colorSec}
                  disabled={!puedeEditar}
                  onChange={(e) => setColorSec(e.target.value)}
                  className="h-9 w-14 cursor-pointer rounded border border-slate-300 disabled:cursor-default"
                />
                <span className="font-mono text-sm text-slate-600">{colorSec}</span>
              </div>
            </Field>
          </div>

          {/* Ver el resultado vale más que dos códigos hex. */}
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-center">
            <p className="text-[11px] uppercase tracking-wide text-slate-400">
              Así se ve en el login
            </p>
            <p className="mt-1 text-lg font-extrabold leading-tight tracking-tight">
              {(() => {
                const [tipo, propio] = partirNombre(nombre || tenant.razon_social);
                return (
                  <>
                    {tipo && <span style={{ color: colorSec }}>{tipo} </span>}
                    <span style={{ color }}>{propio}</span>
                  </>
                );
              })()}
            </p>
            {lema.trim() && (
              <p className="text-sm font-semibold italic" style={{ color: colorSec }}>
                {lema}
              </p>
            )}
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <h2 className="flex items-center gap-2 font-semibold text-slate-900">
            <Building2 className="h-4 w-4 text-slate-400" aria-hidden="true" />
            Identidad
          </h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Datos fiscales y plan contratado. Para cambiarlos, contacta con soporte de la
            plataforma: afectan a la facturación electrónica.
          </p>
        </CardHeader>
        <CardBody>
          <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
            <Dato k="RUC" v={tenant.ruc} mono />
            <Dato k="Razón social" v={tenant.razon_social} />
            <Dato k="Régimen tributario" v={tenant.regimen_tributario} />
            <Dato k="Plan" v={tenant.plan_codigo} />
            <Dato k="Dirección web" v={`/${tenant.slug}`} mono />
          </dl>
        </CardBody>
      </Card>

      {puedeEditar && (
        <div className="flex items-center justify-end gap-3">
          {guardado && (
            <span className="flex items-center gap-1.5 text-sm text-[color:var(--color-link)]">
              <Check className="h-4 w-4" aria-hidden="true" />
              Ajustes guardados
            </span>
          )}
          <Button onClick={() => guardar.mutate()} disabled={!sucio || guardar.isPending}>
            Guardar cambios
          </Button>
        </div>
      )}
    </div>
  );
}

function Dato({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div>
      <dt className="text-xs text-slate-500">{k}</dt>
      <dd className={`text-slate-800 ${mono ? "font-mono" : ""}`}>{v}</dd>
    </div>
  );
}
